import { MouseEvent } from 'react';
import { Box, BoxProps } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyField } from 'components/elements/EmptyField';
import { CompanyOutline } from 'components/icons/CompanyOutline';
import { FlexRow } from 'components/layout/FlexRow';
import { formatCompanyName, formatCompanyAlias } from 'utils/format';

const containerStyle = {
  alignItems: 'center',
  display: 'flex',
  gap: 'padding_2',
  overflow: 'hidden',
};

const linkStyle = {
  display: 'flex',
  '&:hover': {
    color: 'brand.army-dark',
  },
};

const chipStyle = {
  minWidth: '16px',
};

const labelStyle = {
  color: 'brand.army-dark',
  flexShrink: 1,
  fontSize: '12px',
  fontWeight: '400',
  letterSpacing: '-0.02em',
  lineHeight: '14px',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const labelWithIdStyle = {
  fontWeight: '600',
  _hover: {
    textDecor: 'underline',
  },
};

type ChipIconProps = {
  displayIcon?: boolean;
};
const ChipIcon = ({ displayIcon }: ChipIconProps) => {
  if (!displayIcon) {
    return null;
  }

  return <CompanyOutline style={chipStyle} />;
};

type Props = Omit<BoxProps, 'id'> & {
  id?: string;
  name?: string;
  displayIcon?: boolean;
  showLink?: boolean;
  showEmptyState?: boolean;
};

export const CompanyChip = ({
  id,
  name,
  displayIcon,
  showLink = true,
  showEmptyState = true,
  ...props
}: Props) => {
  if (!id && !name) {
    if (!showEmptyState) {
      return null;
    }

    return (
      <FlexRow justifyContent="center">
        <EmptyField />
      </FlexRow>
    );
  }

  // Use Title Case for aliases (companies without UUID), preserve casing for linked companies
  const companyLabel = id ? formatCompanyName(name) : formatCompanyAlias(name);
  const isLinked = id && showLink;

  if (isLinked) {
    return (
      <Box
        as={RouterLink}
        to={`/companies/${id}`}
        onClick={(e: MouseEvent) => e.stopPropagation()}
        sx={{ ...containerStyle, ...linkStyle }}
        {...props}
      >
        <ChipIcon displayIcon={displayIcon} />
        <Box sx={{ ...labelStyle, ...labelWithIdStyle }} title={companyLabel}>
          {companyLabel}
        </Box>
      </Box>
    );
  }

  return (
    <Box {...props} sx={containerStyle}>
      <ChipIcon displayIcon={displayIcon} />
      <Box sx={labelStyle} title={companyLabel}>
        {companyLabel}
      </Box>
    </Box>
  );
};
